/**
 * @author Красильников Даниил <dkrasilnikov@gmail.com>
 * @link https://t.me/krasilneg
 * 
 * @author Колесник Данил <90.kalias.3@gmail.com>
 * @link https://t.me/kalias_90
 */
import React, { useEffect, useState } from 'react';
import {
  RouterProvider,
  Route,
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
  RoutesProps,
  Routes,
  RouteProps,
} from 'react-router-dom';

import { RootState, useAppSelector } from '@/state/store';
import { AuthenticationStatus } from '@/contracts';

import Registration from './pages/registration/index';
import Login from './pages/authentication/index';
import Projects from './pages/project';
import Profile from './pages/profile';
import ResetPassword from './pages/ResetPassword';
import Invitation from './pages/Invitation';
import VerifyEmailPage from './pages/profile/VerifyEmailPage';
import UpgradeRequired from './pages/UpgradeRequired';
import Admin from '@/admin';
import { createSelector } from '@reduxjs/toolkit';
import { Message } from './state/types/message';
import SharedProjectPage from './pages/project/SharedProjectPage';
import t from '@/utils/translate';

const App = () => {
  const { isAuthenticated, isPending, hasLoginTrace, errors, upgradeRequired } = useAppSelector(
    createSelector(
    [
      (state: RootState) => state.authentication.status === AuthenticationStatus.AUTHENTICATED,
      (state: RootState) => state.authentication.status === AuthenticationStatus.PENDING,
      (state: RootState) => !!state.authentication.authenticationTrace,
      (state: RootState) => state.common.messages,
      (state: RootState) => state.common.applicationUpgradeRequired,
    ],
    (isAuthenticated: boolean, isPending: boolean, hasLoginTrace: boolean, messages: Message[], upgradeRequired: boolean) => ({
      isAuthenticated,
      isPending,
      hasLoginTrace,
      errors: messages.filter((m) => m.type == 'error'),
      upgradeRequired,
    })
    )
  );

  const location = useLocation();

  useEffect(() => {
    if (import.meta.env.VITE_YM_COUNTER_NUMBER) {
      //@ts-ignore
      window.ym(
        import.meta.env.VITE_YM_COUNTER_NUMBER,
        'hit',
        window.location.href,
        {
          title: document.title
        }
      );
    }
  }, [location])

  if (upgradeRequired) {
    return <UpgradeRequired />;
  }

  if (isPending && !upgradeRequired) {
    return (<>{(errors.length && errors.map(({ text }) => text).join('<br/>')) || t('hint.initialization')}</>);
  }

  return <Routes>
    <Route path="/project/*" element={
      isAuthenticated
        ? <Projects />
        : <Navigate to="/login" />
    }/>
    <Route path="/profile/*" element={
      isAuthenticated
        ? <Profile />
        : <Navigate to="/login" />
    }/>
    <Route path="/login/*" element={
      <Login />
    } />
    <Route path="/registration/*" element={
      <Registration />
    }/>
    <Route path="/reset-password/:code" element={
      <ResetPassword />
    }/>
    <Route path="/invitation/:code" element={
      <Invitation/>
    }/>
    <Route path="/confirm-email/:code" element={
      <VerifyEmailPage/>
    }/>
    <Route path="/admin/*" element={
      isAuthenticated
        ? <Admin/>
        : <Navigate to="/login" />
    }/>
    <Route path="/sp/:code" element={
      <SharedProjectPage />
    }/>
    <Route path="/*" element={
      isAuthenticated
        ? <Navigate to="/project" replace />
        : <Navigate to={hasLoginTrace ? '/login' : '/registration'} />
      }
    />
  </Routes>;
}

export default () => {
  const Router = createBrowserRouter(
    createRoutesFromElements(
      <Route path="*" element={<App />} />
    )
  );

  return (<RouterProvider router={Router} />);
};
